import React from 'react';
import type { FC } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { SNIPPETS_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import {
	ANALYTICS_ACTION,
	ANALYTICS_SOURCE,
	ANALYTICS_ORIGIN,
	ANALYTICS_SUBJECT,
	ANALYTICS_SUBJECT_ID,
} from '../constants/snippetAnalytics.ts';
import { useSnippetsActions, useSnippetsState } from '../store/snippetsStore.ts';

import { FirstSnippetCreatedModal } from './FirstSnippetCreatedModal.tsx';
import { SnippetBaseModal } from './SnippetBaseModal.tsx';
import type { SnippetBaseModalFormValues } from './snippetModalFormUtils.ts';
import { getDefaultFormValues, normalizeToDoc } from './snippetModalFormUtils.ts';
import { useSnippetModalClose, SnippetModalEvents } from './useSnippetModalClose.ts';

export const createSnippetModalMessages = defineMessages({
	titleCreate: {
		id: 'snippets.snippetEditorModal.titleCreate',
		defaultMessage: 'Create block template',
		description: 'Title for the create block template modal',
	},
	createButton: {
		id: 'snippets.snippetEditorModal.createButton',
		defaultMessage: 'Create',
		description: 'Create button text for create mode',
	},
	successFlagTitle: {
		id: 'snippets.snippetEditorModal.successFlagTitle',
		defaultMessage: 'Template "{snippetName}" created',
		description: 'Title for the success flag shown when template creation succeeds',
	},
	successFlagDescription: {
		id: 'snippets.snippetEditorModal.successFlagDescription',
		defaultMessage: 'You can now use it from the / menu',
		description: 'Description for the success flag shown when template creation succeeds',
	},
	errorFlagTitle: {
		id: 'snippets.snippetEditorModal.errorFlagTitle',
		defaultMessage: 'Could not create template',
		description: 'Title for the error flag shown when template creation fails',
	},
	errorFlagDescription: {
		id: 'snippets.snippetEditorModal.errorFlagDescription',
		defaultMessage: 'Please try again',
		description: 'Description for the error flag shown when template creation fails',
	},
});

export type CreateSnippetModalProps = {
	onClose: () => void;
	onCloseComplete: () => void;
	adf?: ADFEntity | ADFEntity[];
	contentId: string;
	spaceId: string;
	spaceKey: string;
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
} & WithFlagsProps;

const FORM_KEY = 'create-snippet';

const CreateSnippetModalWithFlags: FC<CreateSnippetModalProps> = ({
	onClose,
	onCloseComplete,
	adf,
	contentId,
	spaceId,
	spaceKey,
	flags,
	editorAPI,
}) => {
	const intl = useIntl();
	const { isSnippetSharingForSiteEnabled = false } = useSnippetsState();
	const snippetsActions = useSnippetsActions();
	const { showModal } = useDialogs();
	const handleClose = useSnippetModalClose(onClose);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		data: onboardingData,
		loading: onboardingLoading,
		error: onboardingError,
	} = useGetOnboardingState([SNIPPETS_ONBOARDING.HAS_SEEN_FIRST_SNIPPET_CREATED_MODAL], false);
	const { hasSeenFirstSnippetCreatedModal } = deserializeState(onboardingData);

	const defaultValues = getDefaultFormValues(isSnippetSharingForSiteEnabled);
	const initialAdf = normalizeToDoc(adf);
	const title = intl.formatMessage(createSnippetModalMessages.titleCreate);
	const submitLabel = intl.formatMessage(createSnippetModalMessages.createButton);

	const onSubmit = async (
		values: SnippetBaseModalFormValues,
		currentAdf: import('@atlaskit/adf-schema').DocNode,
	) => {
		const newSnippet = await snippetsActions.createSnippet(
			values.snippetName,
			currentAdf,
			values.snippetDescription,
			{
				flags,
				intl,
				editorAPI: editorAPI ?? undefined,
				isShared: values.isShared,
			},
		);
		if (newSnippet) {
			handleClose(SnippetModalEvents.CREATED);

			const isOnboardingStateReady = !onboardingLoading && !onboardingError;
			const shouldShowFirstSnippetCreatedModal =
				isOnboardingStateReady && !hasSeenFirstSnippetCreatedModal;
			if (shouldShowFirstSnippetCreatedModal) {
				showModal(FirstSnippetCreatedModal, {});
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: ANALYTICS_SOURCE,
					actionSubject: ANALYTICS_SUBJECT.BUTTON,
					action: ANALYTICS_ACTION.CLOSED,
					actionSubjectId: ANALYTICS_SUBJECT_ID.CREATE_SNIPPET,
					type: 'ui',
					attributes: {
						origin: ANALYTICS_ORIGIN.SNIPPET_EDITOR_MODAL,
						event: SnippetModalEvents.CREATED,
						snippetId: newSnippet.snippetId,
					},
				},
			}).fire();
		}
	};

	return (
		<SnippetBaseModal
			title={title}
			submitLabel={submitLabel}
			formKey={FORM_KEY}
			defaultValues={defaultValues}
			initialAdf={initialAdf}
			onSubmit={onSubmit}
			onClose={() => handleClose(SnippetModalEvents.CLOSED)}
			onCancel={() => handleClose(SnippetModalEvents.CANCELLED)}
			onCloseComplete={onCloseComplete}
			contentId={contentId}
			spaceId={spaceId}
			spaceKey={spaceKey}
			flags={flags}
			isSnippetSharingForSiteEnabled={isSnippetSharingForSiteEnabled}
		/>
	);
};

export const CreateSnippetModal = withFlags(CreateSnippetModalWithFlags);
